import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { palette, darkTheme } from 'theme';
import { MuiThemeProvider } from '@material-ui/core/styles';
import styled from 'styled-components';
import { Link as ScrollLink } from 'react-scroll';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import Hidden from '@material-ui/core/Hidden';
import ChevronDownIcon from 'mdi-material-ui/ChevronDown';
import GradientLinearProgress from 'components/GradientLinearProgress';

const SectionText = ({ heading, idx }) => (
  <Typography variant="subtitle2" noWrap>
    <Typography variant="overline" noWrap inline>
      {String(idx + 1).padStart(2, 0)}.
    </Typography>
    {heading}
  </Typography>
);

const SectionAppBar = styled(AppBar)`
  background: ${palette.primary.main};
  margin-top: ${props => (props.hide ? '-68px' : '0')};
  transition: margin 0.25s ease-in;
`;

const SectionTitleTypography = styled(Typography)`
  margin-right: 20px;
`;

const LogoImg = styled.img`
  margin-right: 10px;
  width: 35px;
`;

const SectionNavigation = ({
  hide,
  title,
  sections,
  show_toc,
  logo,
  theme
}) => {
  const sectionBlocks = _.filter(
    sections,
    block => block.section && block.section?.heading
  );

  const [anchorEl, setAnchorEl] = useState(null);
  const [activeSection, setActiveSection] = useState({
    heading: _.get(sectionBlocks[0], 'section.heading'),
    idx: 0
  });
  const [scroll, setScroll] = useState(0);

  const handleScroll = () => {
    requestAnimationFrame(() => {
      const scrollTotal = document.documentElement.scrollTop - 500;
      const heightWin =
        document.documentElement.scrollHeight -
        document.documentElement.clientHeight -
        1500;
      const scroll = Math.min(scrollTotal / heightWin, 1) * 100;
      setScroll(scroll);
    });
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <MuiThemeProvider theme={theme || darkTheme}>
      <SectionAppBar position="fixed" component="nav" hide={hide}>
        <Toolbar>
          {logo && <LogoImg src={logo} />}
          <Hidden xsDown={show_toc}>
            <SectionTitleTypography variant="subtitle2" noWrap>
              {title}
            </SectionTitleTypography>
          </Hidden>
          {show_toc && (
            <>
              <Button onClick={e => setAnchorEl(e.currentTarget)}>
                {activeSection.heading ? (
                  <SectionText {...activeSection} />
                ) : (
                  ''
                )}{' '}
                <ChevronDownIcon />
              </Button>
              <Menu
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorEl={anchorEl}
                keepMounted
                disablePortal
              >
                {sectionBlocks.map((block, idx) => (
                  <MenuItem
                    component={ScrollLink}
                    to={_.kebabCase(block.section.heading)}
                    href={`#${_.kebabCase(block.section.heading)}`}
                    smooth={true}
                    spy={true}
                    offset={-50}
                    onClick={() => setAnchorEl(null)}
                    selected={activeSection.idx === idx}
                    onSetActive={() =>
                      setActiveSection({
                        heading: block.section.heading,
                        idx: idx
                      })
                    }
                    key={idx}
                  >
                    <SectionText heading={block.section.heading} idx={idx} />
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Toolbar>
        <GradientLinearProgress variant="determinate" value={scroll} />
      </SectionAppBar>
    </MuiThemeProvider>
  );
};

export default SectionNavigation;
